import {
  RFQCreateForm,
  DetailedRFQForm,
} from "@b2b/ui-components";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { rfqService, adminService } from "@b2b/services";
import { XCircle, X } from "lucide-react";

interface ToastState {
  type: "success" | "error";
  message: string;
}

interface CategoryHierarchy {
  id: string;
  name: string;
  slug: string;
  parentId?: string;
  level?: number;
  children?: CategoryHierarchy[];
  productCount?: number;
}

export default function RfqCreatePage() {
  const [mode, setMode] = useState<"quick" | "detailed">("quick");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [categories, setCategories] = useState<CategoryHierarchy[]>([]);
  const navigate = useNavigate();

  // Fetch categories with hierarchy on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await adminService.getCategories(true); // Fetch with hierarchy
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  };

  const handleSubmit = async (formData: Record<string, any>) => {
    try {
      setLoading(true);
      
      // Map form data to API format
      const payload = {
        title: formData.leadName || formData.title || "RFQ Request",
        description: formData.description || "",
        quantity: parseFloat(formData.quantity) || 1,
        unit: formData.unit || "pieces",
        categoryIds: formData.categoryIds || (formData.productCategory ? [formData.productCategory] : []) || [],
        tags: formData.tags || (formData.subProductCategory ? [formData.subProductCategory] : []) || [],
        preferredDeliveryDate: formData.preferredDeliveryDate,
        expiryDate: formData.expiryDate,
        status: formData.saveAsDraft ? "DRAFT" : "OPEN",
      };

      const response = await rfqService.createRfq(payload);
      
      if (response.data?.id) {
        showToast("success", "RFQ created successfully!");
        navigate(`/rfq/${response.data.id}`);
      } else {
        showToast("error", "Failed to create RFQ");
      }
    } catch (error: any) {
      console.error("Error creating RFQ:", error);
      showToast("error", error?.response?.data?.message || "Failed to create RFQ");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async (formData: Record<string, any>) => {
    await handleSubmit({ ...formData, saveAsDraft: true });
  };

  return (
    <div>
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 p-4 rounded-lg shadow-lg max-w-[400px] ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          <XCircle className="w-5 h-5 flex-shrink-0" />
          <p className="flex-1 font-medium text-sm">{toast.message}</p>
          <button
            onClick={() => setToast(null)}
            className="ml-2 hover:opacity-80 transition-opacity flex-shrink-0"
            aria-label="Close notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="p-4">
        {mode === "quick" ? (
          <RFQCreateForm 
            switchToDetailed={() => setMode("detailed")}
            onSubmit={handleSubmit}
            onSaveDraft={handleSaveDraft}
            loading={loading}
            categories={categories}
          />
        ) : (
          <DetailedRFQForm 
            switchToQuick={() => setMode("quick")}
            onSubmit={handleSubmit}
            onSaveDraft={handleSaveDraft}
            loading={loading}
            categories={categories}
          />
        )}
      </div>
    </div>
  );
}
